import './Comment.css'
import React, { useState, useEffect, useRef } from 'react'
import classnames from 'classnames'
import formatMessage from 'format-message'
import { EditSVG, DeleteSVG } from './CommentButtons'
import ExpandableIcon from './ExpandableIcon'
import GenericPopup from '../GenericPopup'

import DrawIcon from '../icons/DrawIcon'
import PointIcon from '../icons/PointIcon'
import AreaIcon from '../icons/AreaIcon'
import HighlightIcon from '../icons/HighlightIcon'
import StrikeoutIcon from '../icons/StrikeoutIcon'
import FreetextIcon from '../icons/FreetextIcon'

export default function Comment({
  annotationId,
  annotationType,
  canDelete = false,
  canEdit,
  contents,
  deletedAt,
  deletedBy,
  iconColor,
  isCommentGroupStacked = false,
  isDeleted,
  isFirst,
  isLastVisibleComment,
  isReplyForm = false,
  isRootComment,
  isSelected,
  numberOfReplies = 0,
  onAcknowledgeDeletion,
  onCommentEscape = () => {},
  onCommentHandlingDelete,
  onCommentSave,
  onDeleteAnnotation,
  onDeletePopupCancel,
  onDeletePopupConfirm,
  placeholder,
  replyFormVisible,
  shouldShowContent,
  user_name,
}) {
  const div = useRef(null)
  const textarea = useRef(null)
  const visibilityInterval = useRef(null)

  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [isExpanded, setIsExpanded] = useState(isSelected)
  const [isShowMoreButtonVisible, setIsShowMoreButtonVisible] = useState(false)

  const handleDeletePopupCancel = () => {
    setIsDeletePopupVisible(false)
    div.current.focus()
    onDeletePopupCancel()
    onCommentHandlingDelete(false)
  }

  const expandAndFocus = (_event) => {
    setIsExpanded(true)
    setIsFocused(true)
  }

  const handleKeyDown = (event) => {
    if (!event.shiftKey && event.key === 'Enter') {
      event.preventDefault()
      const commentGroupDiv = div.current.closest('.CommentGroup')
      if (!(commentGroupDiv instanceof HTMLElement)) {
        return
      }
      commentGroupDiv.focus()
      const isEmpty = textarea.current.value.trim() === ''
      if (isEmpty) {
        return
      }

      const replyButton = commentGroupDiv.querySelector('.Comment-reply-button')
      if (replyButton) {
        // TODO: remove need for double.focus and setTimeout
        setTimeout(() => {
          replyButton && replyButton.focus()
        }, 0)
        return
      }
    } else if (event.key === 'Escape' && textarea.current.value.length === 0) {
      if (contents.length > 0) {
        handleDeleteComment()
      } else {
        handleDeletePopupConfirm()
        onCommentEscape()
      }
    }
  }

  const lastIsSelected = useRef(isSelected)
  useEffect(() => {
    updateTextareaHeight()
    updateShowMoreButtonVisibility()
    const commentWasJustDeselected = lastIsSelected.current && isSelected === false

    const commentWasJustSelected = lastIsSelected.current === false && isSelected

    if (commentWasJustDeselected) {
      setIsExpanded(false)
    } else if (commentWasJustSelected) {
      setIsExpanded(true)
    }
    lastIsSelected.current = isSelected
  }, [isSelected, lastIsSelected.current])

  useEffect(() => {
    if (textarea.current != null) {
      textarea.current.value = contents
    }
  }, [textarea.current, contents])

  useEffect(() => {
    if (isExpanded && isFocused) {
      textarea.current.focus()
    }
  }, [isExpanded, isFocused])

  const handleTextareaFocus = (_event) => {
    setIsExpanded(true)
    setIsFocused(true)
  }

  const handleCommentClick = () => {
    if (canEdit === false) {
      setIsExpanded(true)
    }
  }

  const updateShowMoreButtonVisibility = () => {
    if (textarea.current) {
      setIsShowMoreButtonVisible(
        textarea.current instanceof HTMLTextAreaElement &&
          isExpanded === false &&
          textarea.current.scrollHeight > textarea.current.offsetHeight,
      )
    }
  }

  const handleDeleteComment = () => {
    onCommentHandlingDelete(true)
    if (contents.length === 0 && numberOfReplies === 0) {
      handleDeletePopupConfirm()
    } else {
      setIsDeletePopupVisible(true)
    }
  }

  const handleDeletePopupConfirm = () => {
    setIsDeletePopupVisible(false)
    onDeleteAnnotation(annotationId, isRootComment)
    onDeletePopupConfirm()
    onCommentHandlingDelete(false)
  }

  const handleAcknowledgeDeletion = () => {
    onAcknowledgeDeletion(annotationId, isRootComment)
  }

  const generateRemovedMessage = () => {
    if (deletedAt && deletedBy) {
      return formatMessage('Removed {d,date} by {name}', {
        d: new Date(deletedAt),
        name: deletedBy,
      })
    } else if (deletedBy) {
      return formatMessage('Removed by {name}', {
        name: deletedBy,
      })
    } else if (deletedAt) {
      return formatMessage('Removed {d,date}', {
        d: new Date(deletedAt),
      })
    } else {
      return formatMessage('Removed')
    }
  }

  const lastSavedValue = useRef(null)
  const handleTextareaBlur = () => {
    setIsFocused(false)

    if (textarea.current instanceof HTMLElement === false) {
      return
    }

    const valueHasChanged =
      contents !== textarea.current.value && lastSavedValue.current !== textarea.current.value

    if (textarea.current.value && valueHasChanged) {
      onCommentSave({
        contents: textarea.current.value || '',
        annotationId: annotationId,
      })

      lastSavedValue.current = textarea.current.value || ''
    } else if (!textarea.current.value.trim()) {
      textarea.current.value = contents || ''
    }
    updateShowMoreButtonVisibility()

    // IE 11 & Edge need the blur to be triggered here, otherwise
    // Annotation:handleFocusOut() won't receive the focusout event
    // which will leave the annotation in a weird state.
    textarea.current.blur()
  }

  const updateTextareaHeight = () => {
    if (textarea.current == null) {
      return
    }
    textarea.current.style.height = ''
    if (isExpanded) {
      textarea.current.style.height = `${textarea.current.scrollHeight}px`
    } else {
      const newHeight = Math.min(textarea.current.scrollHeight, 80) + 'px'
      textarea.current.style.height = newHeight
      textarea.current.scrollTop = 0
    }
  }

  const deleteMessage = isRootComment
    ? formatMessage('Are you sure you want to delete this comment and all its replies?')
    : formatMessage('Are you sure you want to delete this comment?')

  const titleClassnames = classnames('Comment-title-username', {
    'Comment-title-usernameExtraMargin': !isFirst,
  })

  const commentClassnames = classnames('Comment', {
    'Comment-deleted': isDeleted,
    'Comment-replyFormVisible': replyFormVisible,
    'Comment-isReplyForm': isReplyForm,
    'Comment-root-comment': isRootComment,
    'Comment-isAtBottom': replyFormVisible === false && isLastVisibleComment,
  })

  const textareaClassnames = classnames('CommentGroup-textarea', {
    'is-selected': isSelected,
    'is-focused': isFocused,
  })

  const isTextareaVisible = () => {
    if (textarea.current) {
      const textareaRect = textarea.current.getBoundingClientRect()
      return textareaRect.top > 0 && textareaRect.bottom > 0
    }
    return false
  }

  useEffect(() => {
    updateTextareaHeight()

    const isNew = typeof contents !== 'string' || contents.length === 0

    if (isNew) {
      setTimeout(() => {
        if (textarea.current && isTextareaVisible() === false) {
          visibilityInterval.current = setTimeout(() => {
            if (textarea.current && isTextareaVisible()) {
              textarea.current.focus()
              clearInterval(visibilityInterval.current)
            }
          }, 500)
        } else {
          textarea.current != null && textarea.current.focus()
        }
      }, 0)
    } else {
      updateShowMoreButtonVisibility()
    }

    return () => {
      clearInterval(visibilityInterval.current)
      handleTextareaBlur()
    }
  }, [])

  let icon

  switch (annotationType) {
    case 'ink':
      icon = <DrawIcon color={iconColor} />
      break
    case 'text':
      icon = <PointIcon color={iconColor} />
      break
    case 'strikeout':
      icon = <StrikeoutIcon color={iconColor} />
      break
    case 'square':
      icon = <AreaIcon color={iconColor} />
      break
    case 'highlight':
      icon = <HighlightIcon color={iconColor} />
      break
    case 'freetext':
      icon = <FreetextIcon color={iconColor} />
      break
  }

  return (
    <li className={commentClassnames} onClick={handleCommentClick} ref={div}>
      {isDeleted && (
        <div className="Comment-deleteMessage">
          <p>{generateRemovedMessage()}</p>
          <button
            aria-label={formatMessage('Acknowledge and remove deleted comment')}
            className="Comment-button inst-button inst-button--flat Acknowledge-remove-deleted-comment-button"
            onClick={handleAcknowledgeDeletion}
          >
            <svg label="Acknowledge delete icon" height="13" width="13" viewBox="0 0 2000 2000">
              <path
                d="M1743.858.012L959.869 783.877 176.005.012 0 176.142l783.74 783.989L0 1743.87 176.005 1920l783.864-783.74L1743.858 1920l176.13-176.13-783.865-783.74 783.865-783.988z"
                strokeWidth="1"
                fillRule="evenodd"
                fill="#FFFFFF"
              />
            </svg>
          </button>
        </div>
      )}
      <div className="Comment-title">
        {isFirst && (
          <div className="Comment-icon" role="presentation">
            {icon}
          </div>
        )}
        <div className={titleClassnames}>{user_name}</div>
        {isSelected && (
          <div className="Comment-edit-buttons">
            {/* hidden for now */}
            {false && isFocused === false && canEdit && (
              <button
                aria-label={formatMessage('Edit comment entry')}
                className="Comment-button inst-button inst-button--flat"
                onClick={expandAndFocus}
              >
                <EditSVG />
              </button>
            )}
            {canDelete && !isDeleted && (
              <button
                aria-label={formatMessage('delete comment entry')}
                className="Comment-button inst-button inst-button--flat Delete-comment-entry-button"
                onClick={handleDeleteComment}
              >
                <DeleteSVG />
              </button>
            )}
          </div>
        )}
      </div>
      {shouldShowContent && (
        <div className="Comment-content">
          <textarea
            className={textareaClassnames}
            defaultValue={contents}
            readOnly={!canEdit}
            onBlur={handleTextareaBlur}
            onFocus={handleTextareaFocus}
            onInput={updateTextareaHeight}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-label={placeholder}
            ref={textarea}
            rows={1}
            spellCheck={isSelected && isFocused}
            style={{
              overflowY: isFocused || isExpanded ? 'inherit' : 'hidden',
            }}
            tabIndex={canEdit || isExpanded ? 0 : -1}
          />

          {isShowMoreButtonVisible && <ExpandableIcon onClick={expandAndFocus} />}
        </div>
      )}
      {isCommentGroupStacked && numberOfReplies > 0 && (
        <div className="Comment-reply-number">
          {numberOfReplies === 1
            ? formatMessage('1 Reply')
            : formatMessage('{numberOfReplies} Replies', {
                numberOfReplies,
              })}
        </div>
      )}
      {isDeletePopupVisible && (
        <GenericPopup
          message={deleteMessage}
          onCancel={handleDeletePopupCancel}
          onConfirm={handleDeletePopupConfirm}
          severity="warning"
        />
      )}
    </li>
  )
}
